import { useMemo, useState } from "react";
import type { Player } from "../models/Player";
import headshotsManifest from "../data/headshotsManifest.json";
import { normalizeName } from "../utils/espn";

type HeadshotsManifest = {
  items?: Record<string, string>;
};

const localHeadshotByNormName = Object.entries(
  (headshotsManifest as HeadshotsManifest).items ?? {}
).reduce<Record<string, string>>((acc, [name, src]) => {
  const norm = normalizeName(name);
  if (norm && src) acc[norm] = src;
  return acc;
}, {});

function pickLocalHeadshotUrl(p: Player): string | undefined {
  const existing = String(p.imageUrl ?? "");
  if (existing.startsWith("/headshots/")) return existing;

  const norm = normalizeName(p.name);
  return norm ? localHeadshotByNormName[norm] : undefined;
}

export function usePlayers(args: { basePlayers: Player[]; initialExtraPlayers?: Player[] }) {
  const { basePlayers, initialExtraPlayers = [] } = args;

  const [extraPlayers, setExtraPlayers] = useState<Player[]>(() => initialExtraPlayers);

  const allPlayersArr = useMemo(() => {
    return [...basePlayers, ...extraPlayers];
  }, [basePlayers, extraPlayers]);

  const playersById = useMemo(() => {
    const map: Record<string, Player> = {};
    for (const p of allPlayersArr) {
      map[p.id] = {
        ...p,
        imageUrl: pickLocalHeadshotUrl(p),
      };
    }
    return map;
  }, [allPlayersArr]);

  return {
    extraPlayers,
    setExtraPlayers,
    allPlayersArr,
    playersById,
  };
}
